import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { get, patch, ApiError } from '../../lib/api';

const PROVIDERS = [
  {
    kind: 'mpgs_online',
    name: 'CrediMax Online Gateway',
    blurb: 'Card charged through the CrediMax payment gateway (MPGS). Works today. Refunds can be issued programmatically for any scheme, including BENEFIT debit.',
    tone: 'emerald',
    available: true,
  },
  {
    kind: 'manual_terminal',
    name: 'CrediMax Terminal — manual',
    blurb: 'Cashier keys the amount into the physical terminal and types the approval code back in. No CrediMax project needed. Refunds are credit-card only and require the terminal RRN.',
    tone: 'sky',
    available: true,
  },
  {
    kind: 'credimax_ecr',
    name: 'CrediMax Terminal — integrated (ECR)',
    blurb: 'POS drives the terminal over RS232/USB. Requires CrediMax to enable ECR, release the protocol spec and certify the integration. Scaffolded and ready — see CREDIMAX_ACTION_PLAN.md.',
    tone: 'amber',
    available: false,
  },
];

/**
 * THE CREDIMAX SWITCH.
 *
 * Changing how a till takes card payments is a dropdown here — no deployment,
 * no code change, and it can be piloted on one terminal before rolling out.
 * This is the whole reason the payment layer was built as an abstraction.
 */
export default function Terminals() {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [tid, setTid] = useState('');

  const terminals = useQuery({ queryKey: ['terminals'], queryFn: () => get<any>('/api/v1/admin/terminals') });
  const providerInfo = useQuery({ queryKey: ['providers'], queryFn: () => get<any>('/api/v1/payments/providers') });

  const mutate = useMutation({
    mutationFn: (v: { id: string; body: any }) => patch(`/api/v1/admin/terminals/${v.id}`, v.body),
    onSuccess: () => { setError(''); setEditing(null); void qc.invalidateQueries({ queryKey: ['terminals'] }); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Update failed'),
  });

  const credimax = providerInfo.data?.credimax;

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold">Terminals &amp; payment rails</h1>
        <p className="text-sm text-gray-400 mt-1">
          Each till chooses how it takes card payments. Switching a till from the online
          gateway to a physical CrediMax terminal is a change here — nothing is redeployed.
        </p>
      </div>

      {credimax && !credimax.configured && (
        <div className="card p-4 border-l-4 border-l-amber-500">
          <div className="font-medium text-amber-300">CrediMax gateway is running in SIMULATOR mode</div>
          <div className="text-sm text-gray-300 mt-1">{credimax.setupHint}</div>
          <div className="text-xs text-gray-500 mt-2">
            Card payments are approved by a deterministic local simulator so the whole POS
            is demonstrable. Amounts ending in .999 decline and .998 error, so you can
            exercise the failure paths.
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        {PROVIDERS.map((p) => {
          const live = providerInfo.data?.providers?.find((x: any) => x.kind === p.kind);
          return (
            <div key={p.kind} className={`card p-4 border-l-4 ${
              p.tone === 'emerald' ? 'border-l-emerald-500'
                : p.tone === 'sky' ? 'border-l-sky-500' : 'border-l-amber-500'
            }`}>
              <div className="font-medium text-sm">{p.name}</div>
              <div className="text-xs text-gray-400 mt-1.5 leading-relaxed">{p.blurb}</div>
              {live && (
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className={`badge ${live.integrated ? 'bg-emerald-900/50 text-emerald-300' : 'bg-gray-800 text-gray-400'}`}>
                    {live.integrated ? 'integrated' : 'manual entry'}
                  </span>
                  {live.refundableSchemes?.length > 0 && (
                    <span className="badge bg-gray-800 text-gray-400" title="Schemes this rail can refund">
                      refunds: {live.refundableSchemes.length} schemes
                    </span>
                  )}
                  {!p.available && <span className="badge bg-amber-900/50 text-amber-300">pending setup</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <div className="text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg px-4 py-3">{error}</div>}

      <div className="card">
        <div className="px-4 py-2 border-b border-gray-800 text-sm font-medium">Tills</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr>
              <th className="th">Store</th><th className="th">Till</th>
              <th className="th">Payment rail</th><th className="th">CrediMax TID</th>
              <th className="th">Receipts</th><th className="th"></th>
            </tr></thead>
            <tbody>
              {terminals.data?.terminals?.map((t: any) => (
                <tr key={t.id}>
                  <td className="td">{t.storeCode}</td>
                  <td className="td">
                    <div>{t.code}</div>
                    <div className="text-[11px] text-gray-500">{t.label}</div>
                  </td>
                  <td className="td">
                    <select
                      className="input py-1 text-sm"
                      value={t.paymentProvider}
                      onChange={(e) => mutate.mutate({ id: t.id, body: { paymentProvider: e.target.value } })}
                    >
                      {PROVIDERS.map((p) => (
                        <option key={p.kind} value={p.kind}>
                          {p.name}{!p.available ? ' (pending)' : ''}
                        </option>
                      ))}
                    </select>
                    {t.paymentProvider === 'credimax_ecr' && (
                      <div className="text-[11px] text-amber-400 mt-1">
                        Not yet enabled — this till cannot take card payments.
                      </div>
                    )}
                  </td>
                  <td className="td">
                    {editing === t.id ? (
                      <div className="flex gap-1">
                        <input className="input py-1 w-32 text-sm" value={tid} placeholder="TID"
                          onChange={(e) => setTid(e.target.value)} />
                        <button className="btn-primary text-xs py-1 px-2"
                          onClick={() => mutate.mutate({ id: t.id, body: { credimaxTid: tid || null } })}>
                          Save
                        </button>
                      </div>
                    ) : (
                      <button className="text-left hover:text-brand"
                        onClick={() => { setEditing(t.id); setTid(t.credimaxTid ?? ''); }}>
                        {t.credimaxTid ?? <span className="text-gray-600">set TID…</span>}
                      </button>
                    )}
                  </td>
                  <td className="td money text-gray-500">{t.receiptSeq}</td>
                  <td className="td">
                    <span className={`badge ${t.active ? 'bg-emerald-900/50 text-emerald-300' : 'bg-gray-800 text-gray-500'}`}>
                      {t.active ? 'active' : 'disabled'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 text-[11px] text-gray-500 border-t border-gray-800 leading-relaxed">
          <strong className="text-gray-400">When CrediMax enables ECR:</strong> record each
          terminal's TID above, switch the rail to "integrated (ECR)", and pilot on one till
          before rolling out store by store. The manual option stays available as the fallback
          if a terminal's cable or integration fails mid-trade.
        </div>
      </div>
    </div>
  );
}
